const REPLACEMENT_PLAN_ATTR_VARIANT_ID = "replacement_plan_variant_id";
const REPLACEMENT_PLAN_ATTR_BASIS = "replacement_plan_basis";
const REPLACEMENT_PLAN_ATTR_CONSENT_AT = "replacement_plan_consent_at";
const REPLACEMENT_PLAN_SOURCE = "cart-replacement-plan";
const REPLACEMENT_PLAN_LOADING_RELEASE_MS = 2500;
const REPLACEMENT_PLAN_ARROW_KEYS = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"];
const REPLACEMENT_PLAN_BLOCKED_CLASS = "cart-replacement-plan-blocked";
const REPLACEMENT_PLAN_SHIELD_CLASS = "cart-replacement-plan__shield";
const REPLACEMENT_PLAN_WALLETS_CLASS = "cart-replacement-plan__wallets";
const REPLACEMENT_PLAN_TRIGGER_SELECTOR = "[data-replacement-plan-blocked], [data-replacement-plan-shield]";

const REPLACEMENT_PLAN_TARGETS = {
  drawer: {
    checkout: "#CartDrawer-Checkout",
    form: "#CartDrawer-Form",
    wallets: ".wt-cart__wallets__buttons",
    errors: "CartDrawer-CartErrors",
  },
  page: {
    checkout: '#main-cart-footer button[name="checkout"]',
    form: "#cart",
    wallets: ".cart__dynamic-checkout-buttons",
    errors: "cart-errors",
  },
};

// A refresh destroys and rebuilds this element, so the "opened but nothing picked"
// state and a consent tick still in flight are handed to the instance that comes back.
const PENDING_CONSENT_TTL_MS = 5000;
let expanded = false;
let pendingConsentAt = null;
let pendingConsentTimer = null;

const rememberConsent = (value) => {
  pendingConsentAt = value;
  clearTimeout(pendingConsentTimer);
  pendingConsentTimer = setTimeout(() => {
    pendingConsentAt = null;
  }, PENDING_CONSENT_TTL_MS);
};

const takePendingConsent = () => {
  if (pendingConsentAt === null) return null;
  const value = pendingConsentAt;
  pendingConsentAt = null;
  clearTimeout(pendingConsentTimer);
  return value;
};

// The plan line exists in the cart the moment cart/add.js returns, but the render
// that reports it is a second or more away. This holds the optimistic "a plan is
// in the cart" truth so checkout is gated from the click task onwards, and so the
// successor instance stays gated if its dataset is momentarily behind.
const PENDING_SELECTION_TTL_MS = 8000;
let pendingSelectionId = "";
let pendingSelectionTimer = null;

const rememberPendingSelection = (variantId) => {
  pendingSelectionId = variantId;
  clearTimeout(pendingSelectionTimer);
  pendingSelectionTimer = null;
  if (!variantId) return;
  pendingSelectionTimer = setTimeout(() => {
    pendingSelectionId = "";
  }, PENDING_SELECTION_TTL_MS);
};

// A keyboard selection re-renders the section; the successor instance refocuses the pill.
const PENDING_FOCUS_TTL_MS = 8000;
let pendingFocusVariantId = "";
let pendingFocusTimer = null;

const rememberPendingFocus = (variantId) => {
  pendingFocusVariantId = variantId;
  clearTimeout(pendingFocusTimer);
  pendingFocusTimer = null;
  if (!variantId) return;
  pendingFocusTimer = setTimeout(() => {
    pendingFocusVariantId = "";
  }, PENDING_FOCUS_TTL_MS);
};

const takePendingFocus = () => {
  const variantId = pendingFocusVariantId;
  rememberPendingFocus("");
  return variantId;
};

// The cart attribute carries a decimal shop-currency amount, not cents.
const formatBasis = (cents) => {
  const amount = parseInt(cents, 10);
  if (!Number.isFinite(amount)) return "";
  return (amount / 100).toFixed(2);
};

// Swym's app embed shows a "save this product for later?" prompt whenever a line
// leaves the cart. It has no product-scoped opt-out, so the prompt naming the plan
// product is dismissed through its own control. See docs/REPLACEMENT-PLAN.md.
const SWYM_WATCH_TTL_MS = 6000;
const SWYM_CONTAINER_SELECTOR = '[class*="swym"]';
const SWYM_DIALOG_SELECTOR = '[role="dialog"], [aria-modal="true"]';
const SWYM_CONTROL_SELECTOR = 'button, a, [role="button"]';
const SWYM_DISMISS_TEXT = /no,?\s*thanks/i;
const SWYM_CLOSE_SELECTOR = '[class*="close"], [aria-label*="close" i], [title*="close" i], [data-dismiss]';
let swymWatcher = null;
let swymWatchTimer = null;
let swymWatchTitle = "";
let swymLoggedMiss = false;
let lastSelectedVariantId = "";

const stopSwymWatch = () => {
  if (swymWatcher) swymWatcher.disconnect();
  swymWatcher = null;
  clearTimeout(swymWatchTimer);
  swymWatchTimer = null;
  swymWatchTitle = "";
};

// Only a real control is ever activated, and never one chosen by position, so
// "Save for later" cannot be clicked on the shopper's behalf.
const findSwymDismissControl = (container) => {
  const controls = Array.from(container.querySelectorAll(SWYM_CONTROL_SELECTOR));
  const decline = controls.find((control) => SWYM_DISMISS_TEXT.test(control.textContent || ""));
  if (decline) return decline;
  return controls.find((control) => control.matches(SWYM_CLOSE_SELECTOR)) || null;
};

const isSwymDialog = (node) => {
  if (node.matches(SWYM_DIALOG_SELECTOR)) return true;
  if (typeof window.getComputedStyle !== "function") return false;
  return window.getComputedStyle(node).position === "fixed";
};

// Deepest match first, so an outer app-root wrapper never wins over the card.
const findSwymPrompt = (root) => {
  if (!(root instanceof Element)) return null;
  const title = swymWatchTitle.toLowerCase();
  const candidates = Array.from(root.querySelectorAll(SWYM_CONTAINER_SELECTOR));
  if (root.matches(SWYM_CONTAINER_SELECTOR)) candidates.unshift(root);

  for (let index = candidates.length - 1; index >= 0; index -= 1) {
    const node = candidates[index];
    if (!(node.textContent || "").toLowerCase().includes(title)) continue;
    if (!isSwymDialog(node)) continue;
    const control = findSwymDismissControl(node);
    if (control) return control;
    if (!swymLoggedMiss) {
      swymLoggedMiss = true;
      console.warn("[cart-replacement-plan] Swym prompt found with no dismiss control; left alone");
    }
  }

  return null;
};

const watchForSwymPrompt = (productTitle) => {
  if (!productTitle || typeof MutationObserver !== "function") return;
  stopSwymWatch();
  swymWatchTitle = productTitle;

  // Swym raises the prompt twice on the line-item remove path, so the watch stays
  // armed for its whole TTL and dismisses every match instead of stopping on the first.
  swymWatcher = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((added) => {
        const control = findSwymPrompt(added);
        if (control) control.click();
      });
    });
  });

  swymWatcher.observe(document.body, { childList: true, subtree: true });
  swymWatchTimer = setTimeout(stopSwymWatch, SWYM_WATCH_TTL_MS);
};

class CartReplacementPlan extends HTMLElement {
  constructor() {
    super();
    this.options = [];
    this.onChange = this.onChange.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onDrawerClose = this.onDrawerClose.bind(this);
    this.onDrawerOpen = this.onDrawerOpen.bind(this);
    this.onFormSubmit = this.onFormSubmit.bind(this);
    this.onBlockedClick = this.onBlockedClick.bind(this);
    this.onWalletsMutation = this.onWalletsMutation.bind(this);
    this.onFooterMutation = this.onFooterMutation.bind(this);
    this.onCartUpdate = this.onCartUpdate.bind(this);
    this.walletsObserver = null;
    this.walletsObserverTarget = null;
    this.footerObserver = null;
    this.cartUpdateUnsubscriber = null;
  }

  connectedCallback() {
    this.context = this.dataset.context === "page" ? "page" : "drawer";
    this.targets = REPLACEMENT_PLAN_TARGETS[this.context];
    this.selectedVariantId = this.dataset.selectedVariant || "";
    this.selected = this.selectedVariantId !== "";
    this.consentAt = this.dataset.consentAt || "";

    const hadPlan = lastSelectedVariantId !== "";
    lastSelectedVariantId = this.selectedVariantId;
    if (this.selected) rememberPendingSelection("");

    this.details = this.querySelector(".js-replacement-plan-details");
    this.consentRow = this.querySelector(".js-replacement-plan-consent-row");
    this.toggleInput = this.querySelector(".js-replacement-plan-toggle");
    this.consentInput = this.querySelector(".js-replacement-plan-consent");
    this.errorElement = this.querySelector(".js-replacement-plan-error");
    this.options = Array.from(this.querySelectorAll(".js-replacement-plan-option"));
    this.form = document.querySelector(this.targets.form);

    this.addEventListener("change", this.onChange);
    this.addEventListener("click", this.onClick);
    this.addEventListener("keydown", this.onKeyDown);
    document.addEventListener("click", this.onBlockedClick, true);
    if (this.context === "drawer") {
      this.drawerCloseEvent = typeof PUB_SUB_EVENTS !== "undefined" ? PUB_SUB_EVENTS.cartDrawerClose : "cart-drawer-close";
      this.drawerOpenEvent = typeof PUB_SUB_EVENTS !== "undefined" ? PUB_SUB_EVENTS.cartDrawerOpen : "cart-drawer-open";
      document.addEventListener(this.drawerCloseEvent, this.onDrawerClose);
      document.addEventListener(this.drawerOpenEvent, this.onDrawerOpen);
    }
    if (this.form) this.form.addEventListener("submit", this.onFormSubmit, true);
    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
    }
    this.observeFooter();

    // The plan line outlives its eligibility for one round trip, so checkout stays
    // gated by the listeners above until the removal lands.
    if (this.dataset.stale === "true") {
      this.applyGating();
      this.removeStalePlan();
      return;
    }

    if (hadPlan && !this.selected) this.armSwymWatch();

    const pending = takePendingConsent();
    if (pending !== null) this.consentAt = pending;

    if (this.selected) {
      expanded = true;
    } else if ((this.dataset.attrVariantId || "") !== "") {
      // A plan line removed from the item list leaves the attribute behind.
      expanded = false;
    }
    if (this.consentInput) this.consentInput.checked = this.consentAt !== "";

    this.render();
    this.syncAttributes();
    this.restoreDrawerTabindex();
    this.restorePendingFocus();

    this.assertGating();

    const planLineCount = parseInt(this.dataset.planLineCount, 10) || 0;
    const planQuantity = parseInt(this.dataset.planQuantity, 10) || 0;
    if (this.selected && (planLineCount > 1 || planQuantity > 1)) {
      this.normalizePlanLines();
    }
  }

  disconnectedCallback() {
    this.removeEventListener("change", this.onChange);
    this.removeEventListener("click", this.onClick);
    this.removeEventListener("keydown", this.onKeyDown);
    document.removeEventListener("click", this.onBlockedClick, true);
    if (this.drawerCloseEvent) {
      document.removeEventListener(this.drawerCloseEvent, this.onDrawerClose);
    }
    if (this.drawerOpenEvent) {
      document.removeEventListener(this.drawerOpenEvent, this.onDrawerOpen);
    }
    if (this.form) this.form.removeEventListener("submit", this.onFormSubmit, true);
    this.stopObservingWallets();
    this.stopObservingFooter();
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    this.cartUpdateUnsubscriber = null;
    clearTimeout(this.loadingTimer);
    cancelAnimationFrame(this.gatingFrame);
    cancelAnimationFrame(this.gatingRetryFrame);
    this.releaseGating();
  }

  render() {
    this.classList.toggle("is-selected", this.selected);
    const hasPlan = this.hasPlan();
    if (this.details) this.details.hidden = !(hasPlan || expanded);
    if (this.consentRow) this.consentRow.hidden = !hasPlan;
    if (this.toggleInput) this.toggleInput.checked = hasPlan || expanded;

    this.options.forEach((option) => {
      const isSelected = option.dataset.variantId === this.selectedVariantId;
      option.classList.toggle("is-selected", isSelected);
      option.setAttribute("aria-checked", isSelected ? "true" : "false");
    });
    this.renderOptionTabindex();

    this.applyGating();
  }

  // The optimistic selection keeps the tab stop through the confirming round trip.
  activeOptionIndex() {
    const activeId = pendingSelectionId || this.selectedVariantId;
    const index = this.options.findIndex((option) => option.dataset.variantId === activeId);
    return index < 0 ? 0 : index;
  }

  // The radiogroup owns the pills' tabindex, not cart-drawer.js; a closed drawer has no tab stop.
  optionsFocusable() {
    if (this.context === "page") return true;
    const cartDrawer = document.querySelector("cart-drawer");
    return cartDrawer !== null && cartDrawer.hasAttribute("open");
  }

  renderOptionTabindex() {
    if (this.options.length === 0) return;
    const focusable = this.optionsFocusable();
    const activeIndex = this.activeOptionIndex();
    this.options.forEach((option, index) => {
      option.setAttribute("tabindex", focusable && index === activeIndex ? "0" : "-1");
    });
  }

  restorePendingFocus() {
    const variantId = takePendingFocus();
    if (!variantId) return;
    const option = this.options.find((item) => item.dataset.variantId === variantId);
    if (option) option.focus();
  }

  // A refresh brings back markup with tabindex="-1"; the drawer only re-enables
  // its controls on open, so an open drawer would lose keyboard access.
  restoreDrawerTabindex() {
    if (this.context !== "drawer") return;
    const cartDrawer = document.querySelector("cart-drawer");
    if (!cartDrawer || !cartDrawer.hasAttribute("open")) return;
    const selector = cartDrawer.dataset.toggleTabindex;
    if (!selector || typeof setTabindex !== "function") return;
    setTabindex(cartDrawer.querySelectorAll(selector), "0");
  }

  hasPlan() {
    return this.selected || pendingSelectionId !== "";
  }

  isBlocked() {
    return this.hasPlan() && this.consentAt === "";
  }

  applyGating() {
    const blocked = this.isBlocked();
    const checkoutButton = document.querySelector(this.targets.checkout);
    const wallets = document.querySelector(this.targets.wallets);

    if (!checkoutButton && !wallets && !this.gatingRetried) {
      this.gatingRetried = true;
      this.gatingRetryFrame = requestAnimationFrame(() => this.applyGating());
      return;
    }

    if (checkoutButton) {
      if (blocked) {
        checkoutButton.classList.add(REPLACEMENT_PLAN_BLOCKED_CLASS);
        checkoutButton.setAttribute("aria-disabled", "true");
        checkoutButton.dataset.replacementPlanBlocked = "true";
      } else if (checkoutButton.dataset.replacementPlanBlocked === "true") {
        checkoutButton.classList.remove(REPLACEMENT_PLAN_BLOCKED_CLASS);
        checkoutButton.removeAttribute("aria-disabled");
        delete checkoutButton.dataset.replacementPlanBlocked;
      }
    }

    if (!wallets) return;
    if (blocked) {
      this.blockWallets(wallets);
    } else {
      this.unblockWallets(wallets);
    }
  }

  // On the cart page the checkout button and the wallet container live OUTSIDE the
  // refreshed `.js-contents`, so a foreign re-render of the footer can drop the
  // gating markup while this element survives. These three re-assert it.
  assertGating() {
    cancelAnimationFrame(this.gatingFrame);
    this.gatingFrame = requestAnimationFrame(() => {
      if (this.isConnected) this.applyGating();
    });
  }

  onCartUpdate() {
    if (!this.isConnected) return;
    this.assertGating();
  }

  observeFooter() {
    if (this.context !== "page" || typeof MutationObserver !== "function") return;
    const footer = document.getElementById("main-cart-footer");
    if (!footer) return;
    this.stopObservingFooter();
    this.footerObserver = new MutationObserver(this.onFooterMutation);
    this.footerObserver.observe(footer, { childList: true, subtree: true });
  }

  stopObservingFooter() {
    if (this.footerObserver) this.footerObserver.disconnect();
    this.footerObserver = null;
  }

  onFooterMutation() {
    if (!this.isConnected || !this.isBlocked()) return;
    const checkoutButton = document.querySelector(this.targets.checkout);
    if (checkoutButton && checkoutButton.dataset.replacementPlanBlocked === "true") return;
    this.applyGating();
  }

  // Nothing re-applies gating once this element is gone, so it is released here.
  // In the drawer the successor markup is already in place when the disconnected
  // reaction runs, so both branches are no-ops and nothing flashes.
  releaseGating() {
    const checkoutButton = document.querySelector(this.targets.checkout);
    if (checkoutButton && checkoutButton.dataset.replacementPlanBlocked === "true") {
      checkoutButton.classList.remove(REPLACEMENT_PLAN_BLOCKED_CLASS);
      checkoutButton.removeAttribute("aria-disabled");
      delete checkoutButton.dataset.replacementPlanBlocked;
    }

    const wallets = document.querySelector(this.targets.wallets);
    if (wallets) this.unblockWallets(wallets);
  }

  // The shield has to sit outside the inert subtree, so the container gets a
  // positioned wrapper and the shield becomes its sibling.
  wrapWallets(container) {
    const parent = container.parentElement;
    if (parent && parent.classList.contains(REPLACEMENT_PLAN_WALLETS_CLASS)) return parent;
    const wrap = document.createElement("div");
    wrap.className = REPLACEMENT_PLAN_WALLETS_CLASS;
    container.replaceWith(wrap);
    wrap.appendChild(container);
    return wrap;
  }

  blockWallets(container) {
    const wrap = this.wrapWallets(container);
    container.classList.add(REPLACEMENT_PLAN_BLOCKED_CLASS);
    container.setAttribute("inert", "");

    let shield = wrap.querySelector(`.${REPLACEMENT_PLAN_SHIELD_CLASS}`);
    if (!shield) {
      shield = document.createElement("button");
      shield.type = "button";
      shield.className = REPLACEMENT_PLAN_SHIELD_CLASS;
      shield.dataset.replacementPlanShield = "true";
      shield.tabIndex = -1;
      wrap.appendChild(shield);
    }
    shield.setAttribute("aria-label", this.dataset.consentError || "");

    this.observeWallets(wrap);
  }

  unblockWallets(container) {
    this.stopObservingWallets();
    container.classList.remove(REPLACEMENT_PLAN_BLOCKED_CLASS);
    container.removeAttribute("inert");

    const wrap = container.parentElement;
    if (!wrap || !wrap.classList.contains(REPLACEMENT_PLAN_WALLETS_CLASS)) return;
    const shield = wrap.querySelector(`.${REPLACEMENT_PLAN_SHIELD_CLASS}`);
    if (shield) shield.remove();
    wrap.replaceWith(container);
  }

  observeWallets(wrap) {
    if (this.walletsObserver && this.walletsObserverTarget === wrap) return;
    this.stopObservingWallets();
    if (typeof MutationObserver !== "function") return;
    this.walletsObserver = new MutationObserver(this.onWalletsMutation);
    this.walletsObserverTarget = wrap;
    this.walletsObserver.observe(wrap, { childList: true, subtree: true });
  }

  stopObservingWallets() {
    if (this.walletsObserver) this.walletsObserver.disconnect();
    this.walletsObserver = null;
    this.walletsObserverTarget = null;
  }

  // Shopify's own script re-renders the wallet buttons asynchronously.
  onWalletsMutation() {
    if (!this.isBlocked()) return;
    const container = document.querySelector(this.targets.wallets);
    if (!container) return;
    const wrap = container.parentElement;
    const isWrapped = wrap !== null && wrap.classList.contains(REPLACEMENT_PLAN_WALLETS_CLASS);
    const isShielded =
      isWrapped && wrap.querySelector(`.${REPLACEMENT_PLAN_SHIELD_CLASS}`) !== null;
    if (isShielded && container.hasAttribute("inert")) return;
    this.blockWallets(container);
  }

  showError() {
    if (this.errorElement) this.errorElement.hidden = false;
    if (this.consentRow) this.consentRow.classList.add("is-invalid");
    if (this.consentInput) {
      this.consentInput.setAttribute("aria-invalid", "true");
      this.consentInput.focus();
    }
  }

  hideError() {
    if (this.errorElement) this.errorElement.hidden = true;
    if (this.consentRow) this.consentRow.classList.remove("is-invalid");
    if (this.consentInput) this.consentInput.removeAttribute("aria-invalid");
  }

  showCartError() {
    const errors = document.getElementById(this.targets.errors);
    if (errors && window.cartStrings) errors.textContent = window.cartStrings.error;
  }

  onFormSubmit(event) {
    if (!this.isBlocked()) return;
    event.preventDefault();
    event.stopPropagation();
    this.showError();
  }

  // The checkout button keeps a disabled look without the native `disabled`
  // attribute, so a click still reaches this listener and surfaces the error.
  onBlockedClick(event) {
    if (!this.isBlocked()) return;
    const target = event.target;
    if (!target || typeof target.closest !== "function") return;
    const trigger = target.closest(REPLACEMENT_PLAN_TRIGGER_SELECTOR);
    // Only the blocked submit button and the shield — never a link.
    if (!trigger || trigger.tagName === "A") return;
    event.preventDefault();
    event.stopPropagation();
    this.showError();
  }

  onDrawerClose() {
    this.hideError();
    this.renderOptionTabindex();
    if (this.selected) return;
    expanded = false;
  }

  onDrawerOpen() {
    this.renderOptionTabindex();
  }

  // The block now sits inside #CartDrawer-Form / <cart-drawer-items>, whose
  // CartItems change listener would read our checkbox as a quantity field.
  onChange(event) {
    const target = event.target;
    if (!target) return;
    if (target.classList.contains("js-replacement-plan-toggle")) {
      event.stopPropagation();
      this.onToggleChange(target.checked);
    } else if (target.classList.contains("js-replacement-plan-consent")) {
      event.stopPropagation();
      this.onConsentChange(target.checked);
    }
  }

  onClick(event) {
    const link = event.target.closest(".js-replacement-plan-consent-row a");
    if (link) {
      const modified =
        event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
      if (modified) return;
      // The consent row is a <label>: without this the terms link would tick the box on the way out.
      event.preventDefault();
      if (link.target && link.target !== "_self") {
        window.open(link.href, link.target);
      } else {
        window.location.assign(link.href);
      }
      return;
    }

    const option = event.target.closest(".js-replacement-plan-option");
    if (option) this.selectOption(option);
  }

  onKeyDown(event) {
    if (!REPLACEMENT_PLAN_ARROW_KEYS.includes(event.key)) return;
    const current = event.target.closest(".js-replacement-plan-option");
    if (!current || this.options.length < 2) return;
    event.preventDefault();
    const step = event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1 : 1;
    const index = this.options.indexOf(current);
    const next = this.options[(index + step + this.options.length) % this.options.length];
    next.focus();
    this.selectOption(next, true);
  }

  onToggleChange(checked) {
    if (checked) {
      expanded = true;
      this.render();
      return;
    }

    expanded = false;
    if (this.hasPlan()) {
      this.removePlan();
    } else {
      this.render();
    }
  }

  async onConsentChange(checked) {
    const previousConsentAt = this.consentAt;
    this.consentAt = checked ? new Date().toISOString() : "";
    this.dataset.consentAt = this.consentAt;
    rememberConsent(this.consentAt);
    if (checked) this.hideError();
    this.applyGating();

    try {
      await this.updateCart({
        attributes: { [REPLACEMENT_PLAN_ATTR_CONSENT_AT]: this.consentAt },
      });
    } catch (err) {
      console.error("[cart-replacement-plan] consent", err);
      this.consentAt = previousConsentAt;
      this.dataset.consentAt = previousConsentAt;
      rememberConsent(previousConsentAt);
      if (this.consentInput) this.consentInput.checked = previousConsentAt !== "";
      this.applyGating();
      this.showCartError();
    }
  }

  // Every plan line is read from the live cart, so a duplicate left by a failed
  // request or a second tab is always cleared, whatever the DOM claims.
  async planVariantIds() {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw new Error("Cart read failed");
    const cart = await response.json();
    const productId = parseInt(this.dataset.productId, 10);
    const ids = new Set();

    cart.items.forEach((item) => {
      if (item.product_id !== productId) return;
      ids.add(String(item.variant_id || item.id));
    });

    return Array.from(ids);
  }

  async planUpdates(keepVariantId) {
    const updates = {};
    let ids = [];

    try {
      ids = await this.planVariantIds();
    } catch (err) {
      console.error("[cart-replacement-plan] cart read", err);
      const rendered = this.selectedVariantId || this.dataset.selectedVariant || "";
      if (rendered) ids = [rendered];
    }

    ids.forEach((id) => {
      if (id !== keepVariantId) updates[id] = 0;
    });
    if (keepVariantId) updates[keepVariantId] = 1;

    return updates;
  }

  async selectOption(option, keyboard = false) {
    const variantId = option.dataset.variantId;
    if (!variantId) return;
    if (variantId === this.selectedVariantId || variantId === pendingSelectionId) return;
    if (keyboard) rememberPendingFocus(variantId);

    // The store's cart validation rejects any mutation that leaves two plan lines,
    // so a swap removes every plan line before the new one is added. Read before
    // the optimistic stash is armed, which would make hasPlan() always true.
    const swapping = this.hasPlan();
    let planAdded = false;
    let planRemoved = false;

    this.setLoading(true);
    // Synchronous, still inside the click task: render() -> applyGating() arms the
    // button, the wallets and the form interception before any request goes out.
    rememberPendingSelection(variantId);
    this.render();

    let sentConsentAt = this.consentAt;

    try {
      if (swapping) {
        const updates = await this.planUpdates("");
        if (Object.keys(updates).length > 0) this.armSwymWatch();
        const attributes = this.planAttributes(variantId);
        sentConsentAt = attributes[REPLACEMENT_PLAN_ATTR_CONSENT_AT];
        await this.updateCart({ attributes, updates });
        planRemoved = true;
        await this.addPlan(variantId);
      } else {
        await this.addPlan(variantId);
        planAdded = true;
        const updates = await this.planUpdates(variantId);
        if (Object.keys(updates).length > 1) this.armSwymWatch();
        const attributes = this.planAttributes(variantId);
        sentConsentAt = attributes[REPLACEMENT_PLAN_ATTR_CONSENT_AT];
        await this.updateCart({ attributes, updates });
      }

      this.selectedVariantId = variantId;
      this.selected = true;
      expanded = true;
      await this.reconcileConsent(sentConsentAt);
      await this.refreshCart();
    } catch (err) {
      console.error("[cart-replacement-plan] select", err);
      this.setLoading(false);

      // The old line went and the new one never landed: the cart holds no plan,
      // so the attributes naming one are cleared and state B is shown again.
      if (planRemoved) {
        await this.clearPlanAttributes();
        this.selectedVariantId = "";
        this.selected = false;
        expanded = true;
        rememberPendingSelection("");
        this.render();
        await this.refreshCart();
        this.showCartError();
        return;
      }

      this.showCartError();
      // Nothing was added, or the cart state is unknown: drop the optimistic
      // block and let the next render decide.
      if (planAdded) {
        this.refreshCart();
      } else {
        rememberPendingSelection("");
        this.render();
      }
    }
  }

  // A tick that landed after the attributes went out can be persisted in either
  // order, so the live consent value is re-sent once the selection has succeeded.
  async reconcileConsent(sentConsentAt) {
    if (this.consentAt === sentConsentAt) return;
    try {
      await this.updateCart({
        attributes: { [REPLACEMENT_PLAN_ATTR_CONSENT_AT]: this.consentAt },
      });
    } catch (err) {
      console.error("[cart-replacement-plan] consent reconcile", err);
    }
  }

  // Built at send time so a consent tick made mid-flight is not overwritten.
  planAttributes(variantId) {
    return {
      [REPLACEMENT_PLAN_ATTR_VARIANT_ID]: variantId,
      [REPLACEMENT_PLAN_ATTR_BASIS]: formatBasis(this.dataset.eligibleSubtotal),
      [REPLACEMENT_PLAN_ATTR_CONSENT_AT]: this.consentAt,
    };
  }

  // Consent survives: the shopper's tick is still theirs if they pick again.
  async clearPlanAttributes() {
    try {
      await this.updateCart({
        attributes: {
          [REPLACEMENT_PLAN_ATTR_VARIANT_ID]: "",
          [REPLACEMENT_PLAN_ATTR_BASIS]: "",
        },
      });
    } catch (err) {
      console.error("[cart-replacement-plan] clear attributes", err);
    }
  }

  armSwymWatch() {
    watchForSwymPrompt(this.dataset.productTitle || "");
  }

  // Any over-quantity of the plan product is repaired on the next render: extra
  // lines, or one line carrying quantity > 1. Keeps the variant named by the cart
  // attribute when it is still in the cart, else the most recently added line.
  async normalizePlanLines() {
    if (this.normalizing) return;
    this.normalizing = true;
    this.setLoading(true);
    if (this.selectedVariantId) rememberPendingSelection(this.selectedVariantId);

    try {
      const ids = await this.planVariantIds();
      const attributeId = this.dataset.attrVariantId || "";
      const keep = ids.includes(attributeId) ? attributeId : ids[ids.length - 1] || "";
      const updates = {};
      ids.forEach((id) => {
        if (id !== keep) updates[id] = 0;
      });
      if (keep) updates[keep] = 1;

      await this.updateCart({ updates });
      await this.refreshCart();
    } catch (err) {
      console.error("[cart-replacement-plan] normalize", err);
      this.setLoading(false);
    }
  }

  async removePlan() {
    rememberPendingSelection("");
    this.armSwymWatch();
    const attributes = {
      [REPLACEMENT_PLAN_ATTR_VARIANT_ID]: "",
      [REPLACEMENT_PLAN_ATTR_BASIS]: "",
      [REPLACEMENT_PLAN_ATTR_CONSENT_AT]: "",
    };
    this.setLoading(true);

    try {
      const updates = await this.planUpdates("");
      await this.updateCart({ attributes, updates });

      this.selectedVariantId = "";
      this.selected = false;
      this.consentAt = "";
      this.dataset.consentAt = "";
      rememberConsent("");
      if (this.consentInput) this.consentInput.checked = false;
      await this.refreshCart();
      return true;
    } catch (err) {
      console.error("[cart-replacement-plan] remove", err);
      this.setLoading(false);
      this.render();
      this.showCartError();
      return false;
    }
  }

  // The stale block is rendered hidden, so a failed removal would leave checkout
  // gated with no control on screen. One attempt per connect: on failure the
  // pre-emptive stale gating is dropped and the block is revealed as an ordinary
  // selected block, so the shopper can untick the toggle to retry or consent.
  async removeStalePlan() {
    if (this.staleRemovalAttempted) return;
    this.staleRemovalAttempted = true;

    if (await this.removePlan()) return;

    this.releaseGating();
    delete this.dataset.stale;
    this.hidden = false;
    expanded = true;
    if (this.consentInput) this.consentInput.checked = this.consentAt !== "";
    this.render();
    this.restoreDrawerTabindex();
  }

  syncAttributes() {
    if (this.hasSynced) return;
    this.hasSynced = true;

    const desired = {
      [REPLACEMENT_PLAN_ATTR_VARIANT_ID]: this.selected ? this.selectedVariantId : "",
      [REPLACEMENT_PLAN_ATTR_BASIS]: this.selected ? formatBasis(this.dataset.eligibleSubtotal) : "",
      [REPLACEMENT_PLAN_ATTR_CONSENT_AT]: this.selected ? this.consentAt : "",
    };
    const current = {
      [REPLACEMENT_PLAN_ATTR_VARIANT_ID]: this.dataset.attrVariantId || "",
      [REPLACEMENT_PLAN_ATTR_BASIS]: this.dataset.attrBasis || "",
      [REPLACEMENT_PLAN_ATTR_CONSENT_AT]: this.dataset.consentAt || "",
    };

    const keys = Object.keys(desired);
    if (keys.every((key) => desired[key] === current[key])) return;

    this.dataset.attrVariantId = desired[REPLACEMENT_PLAN_ATTR_VARIANT_ID];
    this.dataset.attrBasis = desired[REPLACEMENT_PLAN_ATTR_BASIS];
    this.dataset.consentAt = desired[REPLACEMENT_PLAN_ATTR_CONSENT_AT];
    this.consentAt = desired[REPLACEMENT_PLAN_ATTR_CONSENT_AT];

    this.updateCart({ attributes: desired }).catch((err) => {
      console.error("[cart-replacement-plan] attribute sync", err);
    });
  }

  async addPlan(variantId) {
    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || "Add to cart failed");
    }
    return response.json();
  }

  async updateCart(body) {
    const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || "Cart update failed");
    }
    return response.json();
  }

  async refreshCart() {
    try {
      await this.refreshCartRegions();
    } finally {
      this.clearLoadingLater();
    }
  }

  async refreshCartRegions() {
    if (this.context === "page") {
      await this.refreshCartPage();
    } else {
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer && typeof cartDrawer.refreshCartDrawer === "function") {
        cartDrawer.refreshCartDrawer();
      }
    }

    if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: REPLACEMENT_PLAN_SOURCE });
    }

    this.assertGating();
  }

  // The drawer refresh is fire-and-forget and swallows its own fetch error, so
  // nothing here can await it. This releases the block if it is still on screen.
  clearLoadingLater() {
    clearTimeout(this.loadingTimer);
    this.loadingTimer = setTimeout(() => {
      if (this.isConnected) this.setLoading(false);
    }, REPLACEMENT_PLAN_LOADING_RELEASE_MS);
  }

  async refreshCartPage() {
    const itemsElement = document.getElementById("main-cart-items");
    const footerElement = document.getElementById("main-cart-footer");
    const bubbleElement = document.getElementById("cart-icon-bubble");
    const sectionIds = [];
    if (itemsElement?.dataset.id) sectionIds.push(itemsElement.dataset.id);
    if (footerElement?.dataset.id) sectionIds.push(footerElement.dataset.id);
    if (sectionIds.length === 0) return;
    if (bubbleElement) sectionIds.push("cart-icon-bubble");

    try {
      const response = await fetch(
        `${window.Shopify.routes.root}?sections=${sectionIds.join(",")}`,
      );
      const data = await response.json();

      const regions = [
        { key: itemsElement?.dataset.id, element: itemsElement, selector: ".js-contents", cart: true },
        { key: footerElement?.dataset.id, element: footerElement, selector: ".js-contents", cart: true },
        // `cart-icon-bubble` is a fetch-only section mirroring the header anchor's
        // markup; cart.js swaps it into the anchor itself. Same handling here.
        { key: "cart-icon-bubble", element: bubbleElement, selector: ".shopify-section", cart: false },
      ];
      const parsedRegions = [];

      for (const region of regions) {
        if (!region.key || !region.element || !data[region.key]) continue;
        const parsed = new DOMParser().parseFromString(data[region.key], "text/html");
        const incoming = parsed.querySelector(region.selector);
        // An emptied cart renders the footer without its wrapper; only a full
        // render can drop the footer and swap in the empty-cart markup.
        if (!incoming && region.cart) {
          window.location.reload();
          return;
        }
        if (!incoming) continue;
        const existing = region.element.querySelector(region.selector) || region.element;
        parsedRegions.push({ existing, incoming });
      }

      parsedRegions.forEach((region) => {
        region.existing.innerHTML = region.incoming.innerHTML;
      });
    } catch (err) {
      console.error("[cart-replacement-plan] section refresh", err);
    }
  }

  setLoading(loading) {
    this.classList.toggle("is-loading", loading);
    if (this.toggleInput) this.toggleInput.disabled = loading;
    if (this.options) {
      this.options.forEach((option) => {
        option.disabled = loading;
      });
    }
  }
}

if (!customElements.get("cart-replacement-plan")) {
  customElements.define("cart-replacement-plan", CartReplacementPlan);
}
